'use client';

import { useState } from 'react';
import { Loader2, Play, Sparkles, CheckCircle, RefreshCw, Pencil } from 'lucide-react';

interface Props {
  characterId: string;
  characterName: string;
  savedInstruct: string | null;
  savedSeed: number | null;
  voiceboxOnline: boolean;
  onDesignSaved: () => void;
}

const EXAMPLE_DESCRIPTIONS = [
  'Deep and slow, like an ancient dragon who has seen a thousand years',
  'High and bubbly, speaks fast and sounds always excited',
  'Quiet and mysterious, like someone sharing a secret',
  'Gruff and no-nonsense, like a tough old warrior',
  'Soft and kind, like a gentle healer or wise mentor',
  'Scratchy and mischievous, like a cunning trickster',
];

const DEFAULT_PREVIEW_LINE = 'The adventure begins today. Are you ready for what comes next?';

function randomSeed() {
  return Math.floor(Math.random() * 999999) + 1;
}

export default function VoiceDesigner({
  characterId,
  characterName,
  savedInstruct,
  savedSeed,
  voiceboxOnline,
  onDesignSaved,
}: Props) {
  const [mode, setMode] = useState<'view' | 'edit'>(savedInstruct ? 'view' : 'edit');
  const [instruct, setInstruct] = useState(savedInstruct ?? '');
  const [seed, setSeed] = useState<number>(savedSeed ?? randomSeed());
  const [previewLine, setPreviewLine] = useState(DEFAULT_PREVIEW_LINE);
  const [audioPath, setAudioPath] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadModel, setDownloadModel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewCount, setPreviewCount] = useState(0);

  async function handlePreview() {
    if (!instruct.trim() || !previewLine.trim()) return;
    setPreviewing(true);
    setError(null);
    setAudioPath(null);

    // New description = new seed for a fresh voice
    const currentSeed = seed;

    try {
      const res = await fetch('/api/voice/design-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId, instructText: instruct.trim(), seed: currentSeed, text: previewLine }),
      });

      if (res.status === 202) {
        const data = await res.json();
        setDownloading(true);
        setDownloadModel(data.modelName ?? 'VoiceDesign model');
        return;
      }

      if (!res.ok) throw new Error((await res.json()).error ?? 'Something went wrong');

      const { audioPath } = await res.json();
      setAudioPath(`/${audioPath}?t=${Date.now()}`);
      setPreviewCount(c => c + 1);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setPreviewing(false);
    }
  }

  function handleTryDifferent() {
    setSeed(randomSeed());
    setAudioPath(null);
    setPreviewCount(0);
  }

  async function handleSave() {
    if (!instruct.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/voice/save-design', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId, instructText: instruct.trim(), seed }),
      });
      if (!res.ok) throw new Error((await res.json()).error ?? 'Save failed');
      setMode('view');
      onDesignSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (!voiceboxOnline) {
    return (
      <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-500/20 rounded-lg px-3 py-2">
        Voicebox is offline — can&apos;t design voices right now.
      </p>
    );
  }

  // ── Model downloading ──
  if (downloading) {
    return (
      <div className="space-y-2 bg-slate-800/50 rounded-xl p-4 text-center">
        <Loader2 size={24} className="animate-spin text-purple-400 mx-auto" />
        <p className="text-sm font-bold text-white">Downloading the Voice Design model…</p>
        <p className="text-xs text-slate-400">This only happens once — it&apos;s about 4GB and may take a few minutes.<br />Come back and try again once it&apos;s done!</p>
        <p className="text-xs text-slate-500 font-mono">{downloadModel}</p>
      </div>
    );
  }

  // ── View mode: design is saved ──
  if (mode === 'view') {
    return (
      <div className="space-y-3">
        <div className="flex items-start gap-2 bg-slate-800/60 rounded-xl px-4 py-3">
          <CheckCircle size={16} className="text-emerald-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-emerald-400 mb-1">Voice designed!</p>
            <p className="text-xs text-slate-300 leading-relaxed italic">&ldquo;{savedInstruct}&rdquo;</p>
          </div>
        </div>

        {/* Hear it again */}
        <div className="space-y-2">
          <div className="flex gap-2 items-center">
            <input
              value={previewLine}
              onChange={e => setPreviewLine(e.target.value)}
              placeholder="Type something to hear the voice…"
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-purple-500"
            />
            <button
              onClick={async () => {
                if (!previewLine.trim() || !savedInstruct) return;
                setPreviewing(true);
                setError(null);
                setAudioPath(null);
                try {
                  const res = await fetch('/api/voice/design-preview', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ characterId, instructText: savedInstruct, seed: savedSeed ?? seed, text: previewLine }),
                  });
                  if (!res.ok) throw new Error((await res.json()).error ?? 'Preview failed');
                  const { audioPath: ap } = await res.json();
                  setAudioPath(`/${ap}?t=${Date.now()}`);
                } catch (e: any) { setError(e.message); }
                finally { setPreviewing(false); }
              }}
              disabled={previewing || !previewLine.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-700 hover:bg-purple-600 disabled:opacity-40 rounded-lg text-xs font-bold transition-colors shrink-0"
            >
              {previewing ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}
              Hear it
            </button>
          </div>
          {audioPath && <audio controls src={audioPath} className="w-full h-8" />}
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>

        <button
          onClick={() => { setMode('edit'); setInstruct(savedInstruct ?? ''); setSeed(savedSeed ?? randomSeed()); setAudioPath(null); setPreviewCount(0); }}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
        >
          <Pencil size={11} /> Change the voice description
        </button>
      </div>
    );
  }

  // ── Edit mode ──
  return (
    <div className="space-y-4">

      {error && (
        <p className="text-xs text-red-400 bg-red-900/20 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
      )}

      {/* Description input */}
      <div className="space-y-2">
        <p className="text-xs text-slate-400 leading-relaxed">
          Describe what {characterName}&apos;s voice sounds like. Think about things like:
          how <span className="text-slate-300">high or low</span> the voice is,
          how <span className="text-slate-300">fast or slow</span> they speak,
          if they sound <span className="text-slate-300">young, old, gentle, or tough</span>,
          and what <span className="text-slate-300">mood</span> they usually carry.
        </p>

        <textarea
          value={instruct}
          onChange={e => { setInstruct(e.target.value); setAudioPath(null); setPreviewCount(0); setSeed(randomSeed()); }}
          placeholder={`Example: "Deep and slow, like an ancient warrior who has seen many battles"`}
          rows={3}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 resize-none focus:outline-none focus:border-purple-500 placeholder:text-slate-600"
        />

        {/* Example suggestions */}
        <div className="space-y-1">
          <p className="text-xs text-slate-600">Need ideas? Click one to use it:</p>
          <div className="flex flex-wrap gap-1.5">
            {EXAMPLE_DESCRIPTIONS.map(ex => (
              <button
                key={ex}
                onClick={() => { setInstruct(ex); setAudioPath(null); setPreviewCount(0); setSeed(randomSeed()); }}
                className="text-xs text-slate-500 hover:text-purple-300 bg-slate-800/50 hover:bg-slate-800 border border-slate-700/50 rounded-full px-2.5 py-1 transition-colors text-left"
              >
                {ex.length > 45 ? ex.slice(0, 45) + '…' : ex}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Preview line */}
      {instruct.trim().length > 10 && (
        <div className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-500">
            Try It Out
          </p>
          <p className="text-xs text-slate-400">
            Type something for {characterName} to say, then click <span className="text-purple-300 font-bold">Hear the Voice</span>. You can keep trying until it sounds just right!
          </p>
          <input
            value={previewLine}
            onChange={e => setPreviewLine(e.target.value)}
            placeholder="Type something for the character to say…"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-purple-500"
          />
          <button
            onClick={handlePreview}
            disabled={previewing || !instruct.trim() || !previewLine.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-purple-700 hover:bg-purple-600 disabled:opacity-40 rounded-lg text-sm font-bold transition-colors"
          >
            {previewing
              ? <><Loader2 size={14} className="animate-spin" /> Generating…</>
              : <><Play size={14} /> Hear the Voice</>}
          </button>
        </div>
      )}

      {/* Audio playback + next steps */}
      {audioPath && (
        <div className="space-y-3 pt-1 border-t border-slate-800">
          <p className="text-xs text-slate-400">
            {previewCount === 1
              ? `Here's what ${characterName} sounds like!`
              : `Preview #${previewCount} — keep going until it's perfect!`}
          </p>
          <audio controls src={audioPath} className="w-full h-8" />

          <div className="flex flex-wrap gap-2 pt-1">
            <button
              onClick={handlePreview}
              disabled={previewing}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs transition-colors"
            >
              <RefreshCw size={11} /> Try same description again
            </button>
            <button
              onClick={handleTryDifferent}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-xs transition-colors"
            >
              <Sparkles size={11} /> Try a different variation
            </button>
          </div>

          <div className="bg-emerald-900/20 border border-emerald-500/20 rounded-xl p-3 space-y-2">
            <p className="text-xs font-bold text-emerald-300">Love this voice?</p>
            <p className="text-xs text-slate-400">
              When you&apos;re happy with how {characterName} sounds, save it! You can always come back and change it later if you want.
            </p>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 rounded-lg text-sm font-bold transition-colors"
            >
              {saving
                ? <><Loader2 size={14} className="animate-spin" /> Saving…</>
                : <><CheckCircle size={14} /> Save This Voice for {characterName}</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
